import { ComponentPropsWithoutRef, forwardRef, useEffect } from 'react';
import cn from 'classnames';

import PopupBody from './PopupBody';
import PopupFooter from './PopupFooter';
import PopupHeader from './PopupHeader';
import PopupTitle from './PopupTitle';
import Button from '@/components/common/Button';

type Props = {
  /** 팝업 표시 여부 */
  open: boolean;

  /** 팝업 크기 */
  size?: 'small' | 'large';

  /** 팝업 닫기 버튼 클릭 시 실행할 함수 */
  onClose?: () => void;

  /** 팝업 외부 클릭 시 실행할 함수 */
  onClickOverlay?: () => void;

  /** 기본적으로는 디자인 가이드에 따라 max-height 이상일 떄 스크롤이 생기지만
   * DatePicker나 Dropdown처럼 팝업밖으로 나가는 요소를 사용하는 경우 예외적으로 overflow_visible을 적용한다.
   * https://kakao.agit.in/g/300069019/wall/407391668#comment_panel_415602757 */
  isOverflowVisible?: boolean;
} & ComponentPropsWithoutRef<'div'>;

const Popup = forwardRef<HTMLDivElement, Props>(
  ({ className, open = false, size, onClose, onClickOverlay, children, isOverflowVisible = false, ...props }, ref) => {
    const handleOnClickOverlay = (event: React.MouseEvent<HTMLDivElement>) => {
      if ((event.target as HTMLElement).classList.contains('comm_layer')) {
        onClickOverlay?.();
      }
    };

    useEffect(() => {
      const htmlElement = document.documentElement;
      const isVerticalScrollExist = htmlElement.scrollHeight > htmlElement.clientHeight;

      if (!isVerticalScrollExist) {
        document.body.style.overflow = open ? 'hidden' : '';
        return () => {
          document.body.style.overflow = '';
        };
      }

      const kakaoHeadElement = document.querySelector('#kakaoHead');
      const containerDocElement = document.querySelector('.container-doc');
      if (!(kakaoHeadElement instanceof HTMLElement) || !(containerDocElement instanceof HTMLElement)) {
        return;
      }

      const onOpen = () => {
        const scrollbarWidth = window.innerWidth - document.body.clientWidth;
        htmlElement.classList.add('layer_on');
        kakaoHeadElement.style.width = 'auto';
        kakaoHeadElement.style.right = `${scrollbarWidth}px`;
        containerDocElement.style.paddingRight = `${scrollbarWidth}px`;
      };

      const onClose = () => {
        htmlElement.classList.remove('layer_on');
        kakaoHeadElement.style.width = '';
        kakaoHeadElement.style.right = '';
        containerDocElement.style.paddingRight = '';
      };

      open ? onOpen() : onClose();

      return onClose;
    }, [open]);

    return (
      <div
        {...props}
        ref={ref}
        className={cn('comm_layer', [className, { show: open }])}
        onClick={handleOnClickOverlay}
      >
        <div className={cn('inner_layer', { [`type_${size}`]: !!size, overflow_visible: isOverflowVisible })}>
          {children}
          {onClose && (
            <Button className="btn_close" onClick={onClose}>
              <span className="ico_wallet ico_close50">닫기</span>
            </Button>
          )}
        </div>
      </div>
    );
  }
);

Popup.displayName = 'Popup';

export default Object.assign(Popup, {
  Header: PopupHeader,
  Title: PopupTitle,
  Body: PopupBody,
  Footer: PopupFooter
});
